import {
  MutableRefObject, ReactElement, useEffect, useRef, useState,
} from 'react';
import { CircularProgress, Tooltip } from '@material-ui/core';
import { GridRowData } from '@material-ui/x-grid';

const HOVER_DELAY = 150;

interface Props {
  containerRef: MutableRefObject<HTMLDivElement | null>;
  rows: GridRowData[];
  reloadKey?: unknown;
  cacheKey?: unknown;
  isRowSupported: (row: GridRowData) => boolean;
  loadContent: (row: GridRowData) => Promise<string | null>;
}

interface AnchorPosition {
  top: number;
  left: number;
}

const DatagridHoverTooltip = (props: Props) => {
  const {
    containerRef, rows, reloadKey, cacheKey, isRowSupported, loadContent,
  } = props;

  const [anchor, setAnchor] = useState<AnchorPosition | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const cache = useRef<Map<number, string | null>>(new Map());
  const pendingIds = useRef<Set<number>>(new Set());
  const hoveredId = useRef<number | null>(null);
  const hoverPosition = useRef<AnchorPosition | null>(null);
  const hoverTimer = useRef<number | null>(null);
  const isTooltipVisible = useRef(false);
  const moveFrame = useRef<number | null>(null);
  const requestGeneration = useRef(0);
  const rowsRef = useRef<GridRowData[]>(rows);
  const isRowSupportedRef = useRef(isRowSupported);
  const loadContentRef = useRef(loadContent);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    isRowSupportedRef.current = isRowSupported;
    loadContentRef.current = loadContent;
  }, [isRowSupported, loadContent]);

  useEffect(() => {
    hoveredId.current = null;
    isTooltipVisible.current = false;
    if (hoverTimer.current !== null) {
      window.clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
    cache.current.clear();
    pendingIds.current.clear();
    setOpen(false);
  }, [reloadKey]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const generation = requestGeneration.current + 1;
    requestGeneration.current = generation;
    cache.current.clear();
    pendingIds.current.clear();
    hoveredId.current = null;

    const clearHoverTimer = () => {
      if (hoverTimer.current !== null) {
        window.clearTimeout(hoverTimer.current);
        hoverTimer.current = null;
      }
    };

    const closeTooltip = () => {
      isTooltipVisible.current = false;
      setOpen(false);
    };

    const hideTooltip = () => {
      clearHoverTimer();
      setLoading(false);
      closeTooltip();
    };

    const getPointerPosition = (event: MouseEvent): AnchorPosition => {
      const containerRect = container.getBoundingClientRect();
      return {
        top: event.clientY - containerRect.top,
        left: event.clientX - containerRect.left,
      };
    };

    const openTooltip = (position: AnchorPosition) => {
      isTooltipVisible.current = true;
      setAnchor(position);
      setOpen(true);
    };

    const showIfStillHovered = (rowId: number) => {
      if (hoveredId.current !== rowId) return;
      const cachedContent = cache.current.get(rowId);
      const pointerPosition = hoverPosition.current;
      setLoading(false);
      if (cachedContent === null || cachedContent === undefined || !pointerPosition) {
        closeTooltip();
        return;
      }
      setContent(cachedContent);
      openTooltip(pointerPosition);
    };

    const showLoadingIfStillHovered = (rowId: number) => {
      if (hoveredId.current !== rowId) return;
      const pointerPosition = hoverPosition.current;
      if (!pointerPosition) return;
      setContent(null);
      setLoading(true);
      openTooltip(pointerPosition);
    };

    const requestRowContent = async (rowId: number) => {
      const row = rowsRef.current.find((gridRow) => Number(gridRow.id) === rowId);
      if (!row || !isRowSupportedRef.current(row)) {
        closeTooltip();
        return;
      }
      if (cache.current.has(rowId)) {
        showIfStillHovered(rowId);
        return;
      }

      showLoadingIfStillHovered(rowId);
      if (pendingIds.current.has(rowId)) return;

      pendingIds.current.add(rowId);
      const resolvedContent = await loadContentRef.current(row);
      pendingIds.current.delete(rowId);
      if (requestGeneration.current !== generation) return;

      cache.current.set(rowId, resolvedContent);
      showIfStillHovered(rowId);
    };

    const handleMouseOver = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const rowElement = target?.closest<HTMLElement>('[role="row"][data-id]') ?? null;
      const rowId = rowElement ? Number(rowElement.getAttribute('data-id')) : 0;
      if (!rowId) {
        hoveredId.current = null;
        hideTooltip();
        return;
      }
      if (rowId === hoveredId.current) return;

      hoveredId.current = rowId;
      hoverPosition.current = getPointerPosition(event);
      hideTooltip();
      hoverTimer.current = window.setTimeout(
        () => requestRowContent(rowId),
        HOVER_DELAY,
      );
    };

    const handleMouseLeave = () => {
      hoveredId.current = null;
      hideTooltip();
    };

    const handleMouseMove = (event: MouseEvent) => {
      hoverPosition.current = getPointerPosition(event);
      if (!isTooltipVisible.current || moveFrame.current !== null) return;
      moveFrame.current = window.requestAnimationFrame(() => {
        moveFrame.current = null;
        const pointerPosition = hoverPosition.current;
        if (!isTooltipVisible.current || !pointerPosition) return;
        setAnchor(pointerPosition);
      });
    };

    const handleMouseDown = () => {
      hoveredId.current = null;
      hideTooltip();
    };

    const handleScroll = () => {
      hoveredId.current = null;
      hideTooltip();
    };

    container.addEventListener('mouseover', handleMouseOver);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseleave', handleMouseLeave);
    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('scroll', handleScroll, true);

    return () => {
      if (requestGeneration.current === generation) {
        requestGeneration.current += 1;
      }
      container.removeEventListener('mouseover', handleMouseOver);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('scroll', handleScroll, true);
      if (moveFrame.current !== null) {
        window.cancelAnimationFrame(moveFrame.current);
        moveFrame.current = null;
      }
      clearHoverTimer();
    };
  }, [cacheKey, containerRef]);

  const getTooltipTitle = (): ReactElement | string => {
    if (loading) {
      return <CircularProgress size={16} />;
    }
    return content ?? '';
  };

  return (
    <Tooltip
      open={open && (loading || content !== null)}
      title={getTooltipTitle()}
      placement="right-start"
      disableFocusListener
      disableTouchListener>
      <div
        style={{
          position: 'absolute',
          top: anchor?.top ?? 0,
          left: anchor?.left ?? 0,
          width: 0,
          height: 0,
          pointerEvents: 'none',
        }} />
    </Tooltip>
  );
};

export default DatagridHoverTooltip;
